import type { Family } from "@/generated/core/v1/family";
import { PensionType } from "@/generated/core/v1/person";

/**
 * Plan accuracy (the prototype's `planAccuracy`): 99 minus the fixed weights of the Sharpen items
 * still to do. The weights are the prototype's; the engine has no sensitivity measure yet
 * (DEVIATIONS.md "Front end vs the prototype"). Whether an item is done is read from the family.
 * The prototype's "Sort transactions" item needs bank connections and is not listed.
 */
export const ACC_FLOOR = 99;

export type SharpenId = "job" | "room" | "work" | "oas";

export const SHARPEN_WEIGHTS: Record<SharpenId, number> = { room: 12, work: 10, job: 9, oas: 4 };

const has = (v: unknown) => v !== undefined && v !== null && v !== "";

export function sharpenDone(family: Family | undefined | null): Record<SharpenId, boolean> {
  const people = family?.persons ?? [];
  const jobs = people.flatMap((p) => p.jobs);
  const pensions = people.flatMap((p) => p.pensions);
  return {
    job: jobs.length === 0 || jobs.every((j) => has(j.occupation) && !!j.startDate?.year),
    room:
      people.length > 0 &&
      people.every(
        (p) =>
          has(p.registeredRoom?.tfsaRoom) &&
          has(p.registeredRoom?.rrspDeductionLimit) &&
          (has(p.registeredRoom?.fhsaParticipationRoom) || !p.isFirstTimeHomeBuyer),
      ),
    // Only asked when someone said there's something at work.
    work:
      pensions.length === 0 ||
      pensions.every(
        (pn) =>
          pn.pensionType === PensionType.NO_PENSION ||
          (pn.pensionType === PensionType.DEFINED_BENEFIT && !!pn.dbDetails) ||
          (pn.pensionType === PensionType.DEFINED_CONTRIBUTION && !!pn.dcDetails),
      ),
    oas: people.length > 0 && people.every((p) => has(p.oasResidency?.yearsInCanadaAfter18)),
  };
}

/** Which items apply to this household at all. */
export function sharpenApplies(family: Family | undefined | null): Record<SharpenId, boolean> {
  const people = family?.persons ?? [];
  return {
    job: people.some((p) => p.jobs.length > 0),
    room: people.length > 0,
    work: people.some((p) => p.pensions.length > 0),
    oas: people.length > 0,
  };
}

export function planAccuracy(family: Family | undefined | null): {
  accuracy: number;
  left: SharpenId[];
} {
  const done = sharpenDone(family);
  const applies = sharpenApplies(family);
  const left = (Object.keys(SHARPEN_WEIGHTS) as SharpenId[]).filter(
    (id) => applies[id] && !done[id],
  );
  const missing = left.reduce((s, id) => s + SHARPEN_WEIGHTS[id], 0);
  return { accuracy: Math.max(0, ACC_FLOOR - missing), left };
}

export function accuracyNote(left: SharpenId[]): string {
  return left.length
    ? `${left.length} answer${left.length === 1 ? "" : "s"} away from ${ACC_FLOOR}%`
    : "As sharp as we can make it";
}
